import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	editSupportDialogMessage,
	getSupportDialogMessages,
	getSupportDialogs,
	replySupportDialog,
	uploadSupportDialogAttachment,
} from "../../api/apiServiceFetch";
import { utcToLocalDateTime } from "../../components/helper";
import ModerationUserCard from "../../components/ModerationUserCard";
import PhotoModal from "../../components/PhotoModal";
import { getFileBaseUrl } from "../../config/environment";
import userPng from "../Reports/user.png";
import "./SupportMessages.scss";

// Presigned attachment links live ~15 minutes. We refresh the thread a bit
// before they die so a screen left open never shows a broken image.
const ATTACHMENT_REFRESH_MARGIN_MS = 30 * 1000;
// Floor between two attachment-driven refetches, so a permanently failing
// image cannot turn into a request loop.
const ATTACHMENT_REFRESH_THROTTLE_MS = 10 * 1000;

const getListFromResponse = (payload) => {
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload)) return payload;
	return [];
};

const formatDateTime = (value) => {
	if (!value) return "";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);

	return utcToLocalDateTime(value);
};

const getAvatarSrc = (dialog) =>
	dialog?.mainPhoto
		? `${getFileBaseUrl()}/Photo/${dialog.userId}/${dialog.mainPhoto}`
		: userPng;

const handleAvatarError = (event) => {
	if (event.currentTarget.src !== userPng) {
		event.currentTarget.src = userPng;
	}
};

const SupportMessages = () => {
	const [dialogs, setDialogs] = useState([]);
	const [dialogsLoading, setDialogsLoading] = useState(true);
	const [dialogsError, setDialogsError] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedDialog, setSelectedDialog] = useState(null);
	const [messages, setMessages] = useState([]);
	const [threadLoading, setThreadLoading] = useState(false);
	const [threadError, setThreadError] = useState("");
	const [reply, setReply] = useState("");
	const [sending, setSending] = useState(false);
	const [sendError, setSendError] = useState("");
	// Photo picked for the next reply: the File plus a local object URL preview.
	const [attachment, setAttachment] = useState(null);
	const [attachmentPreview, setAttachmentPreview] = useState("");
	// Inline editing of an already-sent admin reply.
	const [editingId, setEditingId] = useState(null);
	const [editText, setEditText] = useState("");
	const [savingEdit, setSavingEdit] = useState(false);
	const threadEndRef = useRef(null);
	const fileInputRef = useRef(null);
	const lastThreadLoadAtRef = useRef(0);

	const [isCardOpen, setIsCardOpen] = useState(false);
	const [viewPhoto, setViewPhoto] = useState(null);

	const loadDialogs = useCallback(async (term) => {
		try {
			setDialogsLoading(true);
			setDialogsError("");
			const data = await getSupportDialogs({ searchTerm: term || undefined });
			setDialogs(getListFromResponse(data));
		} catch (err) {
			console.error("Failed to load support dialogs:", err);
			setDialogs([]);
			setDialogsError("Не удалось загрузить диалоги поддержки.");
		} finally {
			setDialogsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadDialogs("");
	}, [loadDialogs]);

	const loadThread = useCallback(async (userId, { silent = false } = {}) => {
		try {
			// A refresh triggered by an expired attachment link must not blank
			// out the thread the moderator is reading.
			if (!silent) setThreadLoading(true);
			setThreadError("");
			lastThreadLoadAtRef.current = Date.now();
			const data = await getSupportDialogMessages(userId);
			const items = getListFromResponse(data).slice().reverse();
			setMessages(items);
			setDialogs((current) =>
				current.map((dialog) =>
					dialog.userId === userId ? { ...dialog, unreadCount: 0 } : dialog,
				),
			);
		} catch (err) {
			console.error("Failed to load support dialog messages:", err);
			// A failed background refresh keeps whatever is already on screen.
			if (!silent) {
				setMessages([]);
				setThreadError("Не удалось загрузить сообщения диалога.");
			}
		} finally {
			if (!silent) setThreadLoading(false);
		}
	}, []);

	useEffect(() => {
		if (threadEndRef.current) {
			threadEndRef.current.scrollIntoView({ block: "end" });
		}
	}, [messages]);

	// Re-signs attachment links shortly before the earliest one expires.
	useEffect(() => {
		const userId = selectedDialog?.userId;
		if (!userId) return undefined;

		const expiries = messages
			.filter((message) => message.attachmentUrl)
			.map((message) => new Date(message.attachmentUrlExpiresAt).getTime())
			.filter((time) => Number.isFinite(time));

		if (expiries.length === 0) return undefined;

		const delay =
			Math.min(...expiries) - Date.now() - ATTACHMENT_REFRESH_MARGIN_MS;
		// Already expired (or a skewed clock): the <img> onError path handles it.
		if (delay <= 0) return undefined;

		const timer = setTimeout(
			() => loadThread(userId, { silent: true }),
			delay,
		);
		return () => clearTimeout(timer);
	}, [messages, selectedDialog, loadThread]);

	// A dead presigned link shows up as a load error — refetch instead of
	// leaving a broken image in the bubble.
	const handleAttachmentError = useCallback(() => {
		const userId = selectedDialog?.userId;
		if (!userId) return;
		if (Date.now() - lastThreadLoadAtRef.current < ATTACHMENT_REFRESH_THROTTLE_MS)
			return;

		loadThread(userId, { silent: true });
	}, [loadThread, selectedDialog]);

	const clearAttachment = useCallback(() => {
		setAttachment(null);
		setAttachmentPreview((current) => {
			if (current) URL.revokeObjectURL(current);
			return "";
		});
		if (fileInputRef.current) fileInputRef.current.value = "";
	}, []);

	// Object URLs are released when the component goes away.
	useEffect(
		() => () => {
			if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
		},
		[attachmentPreview],
	);

	const handleAttachmentChange = useCallback(
		(event) => {
			const file = event.target.files?.[0];
			if (!file) return;

			if (!file.type.startsWith("image/")) {
				setSendError("Можно приложить только изображение.");
				if (fileInputRef.current) fileInputRef.current.value = "";
				return;
			}

			setSendError("");
			setAttachment(file);
			setAttachmentPreview((current) => {
				if (current) URL.revokeObjectURL(current);
				return URL.createObjectURL(file);
			});
		},
		[],
	);

	const handleSelectDialog = useCallback(
		(dialog) => {
			setSelectedDialog(dialog);
			setReply("");
			setSendError("");
			setEditingId(null);
			setEditText("");
			setIsCardOpen(false);
			clearAttachment();
			loadThread(dialog.userId);
		},
		[clearAttachment, loadThread],
	);

	const handleBackToDialogs = useCallback(() => {
		setSelectedDialog(null);
		setMessages([]);
		setThreadError("");
		clearAttachment();
	}, [clearAttachment]);

	const handleSearchSubmit = useCallback(
		(event) => {
			event.preventDefault();
			loadDialogs(searchTerm.trim());
		},
		[loadDialogs, searchTerm],
	);

	const openUserCard = useCallback(() => {
		if (!selectedDialog) return;
		setIsCardOpen(true);
	}, [selectedDialog]);

	const closeUserCard = useCallback(() => {
		setIsCardOpen(false);
	}, []);

	const handleUserDeleted = useCallback(() => {
		setIsCardOpen(false);
		setSelectedDialog(null);
		setMessages([]);
		loadDialogs(searchTerm.trim());
	}, [loadDialogs, searchTerm]);

	const handleCardUpdated = useCallback(() => {
		loadDialogs(searchTerm.trim());
	}, [loadDialogs, searchTerm]);

	const handleReply = useCallback(async () => {
		const trimmed = reply.trim();
		// A photo without words is a valid reply; nothing at all is not.
		if ((!trimmed && !attachment) || !selectedDialog || sending) return;

		try {
			setSending(true);
			setSendError("");

			let uploaded = null;
			if (attachment) {
				// Upload failures are reported separately: the reply itself has
				// not been attempted yet, so "не отправлен ответ" would mislead.
				try {
					uploaded = await uploadSupportDialogAttachment(
						selectedDialog.userId,
						attachment,
					);
				} catch (err) {
					console.error("Failed to upload support attachment:", err);
					setSendError("Не удалось загрузить фото. Попробуйте ещё раз.");
					return;
				}

				if (!uploaded?.isSuccess || !uploaded?.attachmentKey) {
					setSendError(
						uploaded?.message
							? `Не удалось загрузить фото: ${uploaded.message}`
							: "Не удалось загрузить фото.",
					);
					return;
				}
			}

			const sent = await replySupportDialog(
				selectedDialog.userId,
				trimmed,
				uploaded
					? {
							attachmentKey: uploaded.attachmentKey,
							attachmentType: uploaded.type,
						}
					: undefined,
			);
			const message = {
				id: sent?.id ?? -Date.now(),
				message: sent?.message ?? trimmed,
				isFromAdmin: true,
				sentAt: sent?.sentAt ?? new Date().toISOString(),
				attachmentKey: sent?.attachmentKey ?? uploaded?.attachmentKey ?? null,
				attachmentType: sent?.attachmentType ?? uploaded?.type ?? null,
				attachmentUrl: sent?.attachmentUrl ?? null,
				attachmentUrlExpiresAt: sent?.attachmentUrlExpiresAt ?? null,
			};
			setMessages((current) => [...current, message]);
			setDialogs((current) =>
				current.map((dialog) =>
					dialog.userId === selectedDialog.userId
						? {
								...dialog,
								lastMessage: message.message,
								lastMessageAt: message.sentAt,
								lastMessageIsFromAdmin: true,
								lastMessageHasAttachment: Boolean(message.attachmentKey),
							}
						: dialog,
				),
			);
			setReply("");
			clearAttachment();

			// Belt and braces: if the reply came back without a signed link, pull
			// the thread again so the just-sent photo is visible right away.
			if (message.attachmentKey && !message.attachmentUrl) {
				loadThread(selectedDialog.userId, { silent: true });
			}
		} catch (err) {
			console.error("Failed to send support reply:", err);
			setSendError("Не удалось отправить ответ.");
		} finally {
			setSending(false);
		}
	}, [
		attachment,
		clearAttachment,
		loadThread,
		reply,
		selectedDialog,
		sending,
	]);

	const handleReplyKeyDown = useCallback(
		(event) => {
			if (event.key === "Enter" && !event.shiftKey) {
				event.preventDefault();
				handleReply();
			}
		},
		[handleReply],
	);

	const startEditMessage = useCallback((message) => {
		setEditingId(message.id);
		setEditText(message.message ?? "");
		setSendError("");
	}, []);

	const cancelEditMessage = useCallback(() => {
		setEditingId(null);
		setEditText("");
	}, []);

	const saveEditMessage = useCallback(async () => {
		const trimmed = editText.trim();
		if (!trimmed || !selectedDialog || editingId == null || savingEdit) return;

		try {
			setSavingEdit(true);
			setSendError("");
			const updated = await editSupportDialogMessage(
				selectedDialog.userId,
				editingId,
				trimmed,
			);
			const newText = updated?.message ?? trimmed;
			const editedAt = updated?.editedAt ?? new Date().toISOString();
			setMessages((current) =>
				current.map((item) =>
					item.id === editingId
						? { ...item, message: newText, editedAt }
						: item,
				),
			);
			// If the edited reply is the latest message, refresh the sidebar preview.
			setDialogs((current) =>
				current.map((dialog) =>
					dialog.userId === selectedDialog.userId &&
					dialog.lastMessageIsFromAdmin
						? { ...dialog, lastMessage: newText }
						: dialog,
				),
			);
			setEditingId(null);
			setEditText("");
		} catch (err) {
			console.error("Failed to edit support reply:", err);
			setSendError("Не удалось сохранить изменения.");
		} finally {
			setSavingEdit(false);
		}
	}, [editText, selectedDialog, editingId, savingEdit]);

	const handleEditKeyDown = useCallback(
		(event) => {
			if (event.key === "Enter" && !event.shiftKey) {
				event.preventDefault();
				saveEditMessage();
			}
			if (event.key === "Escape") {
				event.preventDefault();
				cancelEditMessage();
			}
		},
		[saveEditMessage, cancelEditMessage],
	);

	return (
		<div className="support-messages">
			<h2>Чат поддержки</h2>

			{viewPhoto && (
				<PhotoModal photo={viewPhoto} onClose={() => setViewPhoto(null)} />
			)}

			<ModerationUserCard
				user={selectedDialog}
				isOpen={isCardOpen}
				onClose={closeUserCard}
				onUserDeleted={handleUserDeleted}
				onUserUpdated={handleCardUpdated}
			/>

			<div
				className={`support-chat${
					selectedDialog ? " support-chat--thread-open" : ""
				}`}
			>
				<aside className="support-chat__sidebar">
					<form className="support-chat__search" onSubmit={handleSearchSubmit}>
						<input
							type="text"
							placeholder="Поиск по имени или сообщению"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value)}
						/>
						<button type="submit">Найти</button>
					</form>

					{dialogsLoading && (
						<div className="support-messages__status">Загрузка...</div>
					)}
					{dialogsError && !dialogsLoading && (
						<div className="support-messages__status support-messages__status--error">
							{dialogsError}
						</div>
					)}
					{!dialogsLoading && !dialogsError && dialogs.length === 0 && (
						<div className="support-messages__status">Нет диалогов</div>
					)}

					<div className="support-chat__dialogs">
						{dialogs.map((dialog) => (
							<button
								key={dialog.userId}
								type="button"
								className={`support-chat__dialog${
									selectedDialog?.userId === dialog.userId
										? " support-chat__dialog--active"
										: ""
								}`}
								onClick={() => handleSelectDialog(dialog)}
							>
								<img
									className="support-chat__avatar"
									src={getAvatarSrc(dialog)}
									onError={handleAvatarError}
									alt=""
								/>
								<div className="support-chat__dialog-body">
									<div className="support-chat__dialog-top">
										<span className="support-chat__dialog-name">
											{dialog.sender || `User #${dialog.userId}`}
										</span>
										<span className="support-chat__dialog-time">
											{formatDateTime(dialog.lastMessageAt)}
										</span>
									</div>
									<div className="support-chat__dialog-bottom">
										<span className="support-chat__dialog-preview">
											{dialog.lastMessage || dialog.lastMessageHasAttachment ? (
												<>
													{dialog.lastMessageIsFromAdmin ? "Вы: " : ""}
													{dialog.lastMessageHasAttachment && (
														<span className="support-chat__dialog-photo-mark">
															Фото
														</span>
													)}
													{dialog.lastMessage}
												</>
											) : (
												<em>Нет сообщений</em>
											)}
										</span>
										{dialog.unreadCount > 0 && (
											<span className="support-chat__dialog-unread">
												{dialog.unreadCount}
											</span>
										)}
									</div>
								</div>
							</button>
						))}
					</div>
				</aside>

				<section className="support-chat__thread">
					{!selectedDialog && (
						<div className="support-chat__placeholder">
							Выберите диалог слева, чтобы открыть переписку.
						</div>
					)}

					{selectedDialog && (
						<>
							<div className="support-chat__thread-header">
								<button
									type="button"
									className="support-chat__thread-back"
									onClick={handleBackToDialogs}
									title="К списку диалогов"
									aria-label="К списку диалогов"
								>
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M10 13L5 8L10 3"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
								<button
									type="button"
									className="support-chat__thread-user"
									onClick={openUserCard}
									title="Открыть карточку пользователя"
								>
									<img
										className="support-chat__avatar support-chat__avatar--header"
										src={getAvatarSrc(selectedDialog)}
										onError={handleAvatarError}
										alt=""
									/>
									<span className="support-chat__thread-name">
										{selectedDialog.sender || `User #${selectedDialog.userId}`}
									</span>
								</button>
								<span className="support-chat__thread-meta">
									ID: {selectedDialog.userId}
								</span>
								<button
									type="button"
									className="support-chat__thread-card-button"
									onClick={openUserCard}
								>
									Карточка
								</button>
							</div>

							<div className="support-chat__messages">
								{threadLoading && (
									<div className="support-messages__status">Загрузка...</div>
								)}
								{threadError && !threadLoading && (
									<div className="support-messages__status support-messages__status--error">
										{threadError}
									</div>
								)}
								{!threadLoading && !threadError && messages.length === 0 && (
									<div className="support-messages__status">
										В этом диалоге пока нет сообщений
									</div>
								)}
								{!threadLoading &&
									!threadError &&
									messages.map((message) => (
										<div
											key={message.id}
											className={`support-chat__message${
												message.isFromAdmin
													? " support-chat__message--admin"
													: ""
											}`}
										>
											<div className="support-chat__bubble">
												{editingId === message.id ? (
													<div className="support-chat__bubble-edit">
														<textarea
															rows={3}
															value={editText}
															onChange={(event) =>
																setEditText(event.target.value)
															}
															onKeyDown={handleEditKeyDown}
															autoFocus
														/>
														<div className="support-chat__bubble-edit-actions">
															<button
																type="button"
																disabled={!editText.trim() || savingEdit}
																onClick={saveEditMessage}
															>
																{savingEdit ? "Сохранение..." : "Сохранить"}
															</button>
															<button
																type="button"
																className="support-chat__bubble-edit-cancel"
																disabled={savingEdit}
																onClick={cancelEditMessage}
															>
																Отмена
															</button>
														</div>
													</div>
												) : (
													<>
														{message.attachmentUrl && (
															<button
																type="button"
																className="support-chat__bubble-photo"
																onClick={() =>
																	setViewPhoto(message.attachmentUrl)
																}
																title="Открыть фото"
															>
																<img
																	src={message.attachmentUrl}
																	onError={handleAttachmentError}
																	alt="Вложение"
																/>
															</button>
														)}
														{!message.attachmentUrl &&
															message.attachmentKey && (
																<div className="support-chat__bubble-text support-chat__bubble-text--muted">
																	Фото недоступно
																</div>
															)}
														{message.message ? (
															<div className="support-chat__bubble-text">
																{message.message}
															</div>
														) : null}
														<div className="support-chat__bubble-time">
															{message.editedAt ? "изменено · " : ""}
															{formatDateTime(message.sentAt)}
															{message.isFromAdmin && (
																<button
																	type="button"
																	className="support-chat__bubble-edit-button"
																	title="Редактировать сообщение"
																	onClick={() => startEditMessage(message)}
																>
																	<svg
																		width="12"
																		height="12"
																		viewBox="0 0 24 24"
																		fill="none"
																		xmlns="http://www.w3.org/2000/svg"
																	>
																		<path
																			d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
																			stroke="currentColor"
																			strokeWidth="2"
																			strokeLinecap="round"
																			strokeLinejoin="round"
																		/>
																	</svg>
																</button>
															)}
														</div>
													</>
												)}
											</div>
										</div>
									))}
								<div ref={threadEndRef} />
							</div>

							{sendError && (
								<div className="support-messages__status support-messages__status--error">
									{sendError}
								</div>
							)}

							{attachmentPreview && (
								<div className="support-chat__attachment-preview">
									<img src={attachmentPreview} alt="Вложение" />
									<span className="support-chat__attachment-name">
										{attachment?.name}
									</span>
									<button
										type="button"
										className="support-chat__attachment-remove"
										onClick={clearAttachment}
										disabled={sending}
										title="Убрать фото"
									>
										Убрать
									</button>
								</div>
							)}

							<div className="support-chat__composer">
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									className="support-chat__file-input"
									onChange={handleAttachmentChange}
								/>
								<button
									type="button"
									className="support-chat__attach-button"
									disabled={sending}
									onClick={() => fileInputRef.current?.click()}
									title="Прикрепить фото"
									aria-label="Прикрепить фото"
								>
									<svg
										width="18"
										height="18"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M21 12.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 1 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
								<textarea
									placeholder="Введите ответ..."
									rows={2}
									value={reply}
									onChange={(event) => setReply(event.target.value)}
									onKeyDown={handleReplyKeyDown}
								/>
								<button
									type="button"
									disabled={(!reply.trim() && !attachment) || sending}
									onClick={handleReply}
								>
									{sending ? "Отправка..." : "Отправить"}
								</button>
							</div>
						</>
					)}
				</section>
			</div>
		</div>
	);
};

export default SupportMessages;
